/**
 * An "are you sure?" box shown in place, under the button that asked —
 * instead of window.confirm. It takes focus when it appears so keyboard and
 * screen-reader users land on the question; Cancel puts things back.
 */

import React, { useEffect, useId, useRef } from 'react';
import { Button } from '../../ui';

interface Props {
  title: string;
  children?: React.ReactNode;
  confirmLabel: string;
  busyLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
  tone?: 'default' | 'danger';
}

const InlineConfirm: React.FC<Props> = ({ title, children, confirmLabel, busyLabel = 'Saving…', cancelLabel = 'Cancel', onConfirm, onCancel, busy = false, tone = 'default' }) => {
  const titleId = useId();
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
  }, []);
  return (
    <div
      ref={ref}
      role="group"
      aria-labelledby={titleId}
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !busy) {
          e.stopPropagation();
          onCancel();
        }
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: '12px 14px',
        borderRadius: 'var(--radius-md)',
        border: `1px solid ${tone === 'danger' ? 'var(--danger)' : 'var(--border-2)'}`,
        background: tone === 'danger' ? 'var(--danger-soft)' : 'var(--bg-surface-2)',
        outline: 'none',
      }}
    >
      <div id={titleId} style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--fg-1)' }}>
        {title}
      </div>
      {children && <div style={{ fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.5 }}>{children}</div>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button size="sm" variant={tone === 'danger' ? 'destructive' : 'primary'} onClick={onConfirm} disabled={busy}>
          {busy ? busyLabel : confirmLabel}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>
          {cancelLabel}
        </Button>
      </div>
    </div>
  );
};

export default InlineConfirm;
